import {
  normalizeClassicPer100g,
  formatDvPercent,
  formatMcg,
  formatMg,
  formatNutritionValue,
  NF_FOOTNOTE,
} from "@/lib/nutritionLabelModel";

function DvPct({ field, value }) {
  const pct = formatDvPercent(field, value);
  if (pct == null) return <span className="nf-row-pct" />;
  return <span className="nf-row-pct">{pct}%</span>;
}

function buildValues(nutrition) {
  const per100 = normalizeClassicPer100g(nutrition?.per_100g);
  if (!per100) return null;

  const weightG = nutrition?.serving_weight_g || 0;
  const perServe = nutrition?.per_serving;
  const useServing = Boolean(perServe && weightG > 0 && weightG !== 100);
  const values = useServing ? normalizeClassicPer100g(perServe) : per100;

  let servingSizeText = "100g";
  if (useServing) {
    const label = nutrition?.serving_label || `${weightG} g`;
    servingSizeText = label.replace(/\s+/g, "");
  } else if (weightG === 100) {
    servingSizeText = "100g";
  }

  return { values, servingSizeText };
}

/** FDA-style Nutrition Facts — matches individual label PDF. */
export default function CustomerNutritionLabel({ nutrition }) {
  const built = buildValues(nutrition);
  if (!built) return null;

  const { values, servingSizeText } = built;
  const g = (v) => `${formatNutritionValue(v, false)}g`;

  return (
    <section className="customer-nutrition-wrap" aria-label="Nutrition Facts">
      <div className="nf-label">
        <div className="nf-title">Nutrition Facts</div>
        <div className="nf-serving">Serving size {servingSizeText}</div>
        <div className="nf-bar-thick" aria-hidden="true" />

        <div className="nf-amount">Amount per serving</div>
        <div className="nf-calories-row">
          <span className="nf-calories-label">Calories</span>
          <span className="nf-calories-value">{formatNutritionValue(values.energy_kcal, true)}</span>
        </div>
        <div className="nf-bar-medium" aria-hidden="true" />

        <div className="nf-dv-head">% Daily Value*</div>

        <div className="nf-row nf-row-bold">
          <span>Total Fat {g(values.total_fat_g)}</span>
          <DvPct field="total_fat_g" value={values.total_fat_g} />
        </div>
        <div className="nf-row nf-row-indent">
          <span>Saturated Fat {g(values.saturated_fat_g)}</span>
          <DvPct field="saturated_fat_g" value={values.saturated_fat_g} />
        </div>
        <div className="nf-row nf-row-indent">
          <span>
            <em>Trans</em> Fat {g(values.trans_fat_g)}
          </span>
          <DvPct field="trans_fat_g" value={values.trans_fat_g} />
        </div>
        <div className="nf-row nf-row-bold">
          <span>Cholesterol {formatMg(values.cholesterol_mg)}</span>
          <DvPct field="cholesterol_mg" value={values.cholesterol_mg} />
        </div>
        <div className="nf-row nf-row-bold">
          <span>Sodium {formatMg(values.sodium_mg)}</span>
          <DvPct field="sodium_mg" value={values.sodium_mg} />
        </div>
        <div className="nf-row nf-row-bold">
          <span>Total Carbohydrate {g(values.carbohydrate_g)}</span>
          <DvPct field="carbohydrate_g" value={values.carbohydrate_g} />
        </div>
        <div className="nf-row nf-row-indent">
          <span>Dietary Fiber {g(values.dietary_fiber_g)}</span>
          <DvPct field="dietary_fiber_g" value={values.dietary_fiber_g} />
        </div>
        <div className="nf-row nf-row-indent">
          <span>Total Sugars {g(values.total_sugars_g)}</span>
          <span className="nf-row-pct" />
        </div>
        <div className="nf-row nf-row-indent2">
          <span>Includes {g(values.added_sugars_g)} Added Sugars</span>
          <DvPct field="added_sugars_g" value={values.added_sugars_g} />
        </div>
        <div className="nf-row nf-row-bold">
          <span>Protein {g(values.protein_g)}</span>
          <span className="nf-row-pct" />
        </div>

        <div className="nf-bar-thick" aria-hidden="true" />

        <div className="nf-row nf-micro">
          <span>Vitamin D {formatMcg(values.vitamin_d_mcg)}</span>
          <DvPct field="vitamin_d_mcg" value={values.vitamin_d_mcg} />
        </div>
        <div className="nf-row nf-micro">
          <span>Calcium {formatMg(values.calcium_mg)}</span>
          <DvPct field="calcium_mg" value={values.calcium_mg} />
        </div>
        <div className="nf-row nf-micro">
          <span>Iron {formatMg(values.iron_mg)}</span>
          <DvPct field="iron_mg" value={values.iron_mg} />
        </div>
        <div className="nf-row nf-micro">
          <span>Potassium {formatMg(values.potassium_mg)}</span>
          <DvPct field="potassium_mg" value={values.potassium_mg} />
        </div>

        <div className="nf-bar-medium" aria-hidden="true" />
        <p className="nf-footnote">{NF_FOOTNOTE}</p>
      </div>
    </section>
  );
}
